import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, Chip, Alert, Divider, CircularProgress, InputAdornment,
  RadioGroup, Radio, FormControlLabel, Checkbox, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NightComponent, RateCard, RateCardList, RateCardRow, RateCardScope, RateCardVehicle, RateCardVersion,
  getRateCardScope, saveRateCard, deactivateRateCard, getRateCardHistory,
} from '../../services/rateCardService';
import { CardForm, NIGHT_COMPONENT_LABELS, cardToForm, formToCard, isDirty } from './rateCardForm';
import SlabTable from './SlabTable';
import FarePreview from './FarePreview';
import { useToast } from '../../lib/useToast';
import { getApiErrorMessage } from '../../lib/toast';
import { useConfirm } from '../../components/feedback/ConfirmDialog';

const Money: React.FC<{ label: string; value: string; onChange: (v: string) => void; disabled?: boolean; helper?: string; optional?: boolean; unit?: string }> =
  ({ label, value, onChange, disabled, helper, optional, unit = '₹' }) => (
    <TextField
      size="small" type="number" label={label} value={value} disabled={disabled} fullWidth
      placeholder={optional ? 'none' : undefined}
      helperText={helper}
      inputProps={{ min: 0, step: 'any' }}
      InputProps={unit === '₹' ? { startAdornment: <InputAdornment position="start">₹</InputAdornment> }
        : { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
      onChange={(e) => onChange(e.target.value)}
    />
  );

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Box sx={{ mx: -2, mt: -2, mb: 2, px: 2, py: 1, bgcolor: '#ecfdf5', borderBottom: '1px solid #d1fae5', borderTopLeftRadius: 4, borderTopRightRadius: 4 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </Box>
    {children}
  </Paper>
);

const HistoryDialog: React.FC<{ row: RateCardRow | null; onClose: () => void }> = ({ row, onClose }) => {
  const history = useQuery({ queryKey: ['rate-card-history', row?.id], queryFn: () => getRateCardHistory(row!.id), enabled: !!row });
  const versions: RateCardVersion[] = history.data ?? [];
  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change history</DialogTitle>
      <DialogContent dividers>
        {history.isLoading && <CircularProgress size={20} />}
        {versions.map((v) => (
          <Box key={v.id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              v{v.version} · {new Date(v.createdAt).toLocaleString()} · {v.changedBy ?? 'unknown'} {!v.isActive && <Chip size="small" label="removed" sx={{ ml: 1 }} />}
            </Typography>
            {v.note && <Typography variant="body2" color="text.secondary">{v.note}</Typography>}
            <Typography variant="caption" color="text.secondary">
              Base ₹{v.card.baseFare} · {v.card.slabs.map((s) => `${s.fromKm}${s.toKm === null ? '+' : `–${s.toKm}`} km ₹${s.perKm}`).join(' · ')}
              {v.card.time?.perMin ? ` · ₹${v.card.time.perMin}/min` : ''}
            </Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

interface Props {
  vehicle: RateCardVehicle;
  zoneId: number | null;
  /** e.g. "All zones without an override" or "Gomti Nagar · Lucknow". */
  scopeLabel: string;
  list: RateCardList;
  canWrite: boolean;
  onRemoved?: () => void;
}

/** One scope's card: order/extra fare sections, save / remove / history, and the server-side live preview. */
const RateCardEditor: React.FC<Props> = ({ vehicle, zoneId, scopeLabel, list, canWrite, onRemoved }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const scopeQuery = useQuery({
    queryKey: ['rate-card-scope', vehicle.rideOption.id, zoneId],
    queryFn: () => getRateCardScope(vehicle.rideOption.id, zoneId),
  });
  const scope: RateCardScope | undefined = scopeQuery.data;
  const row: RateCardRow | null = scope?.row ?? null;
  const activeRow = row && row.isActive ? row : null;
  const savedCard: RateCard | null = activeRow?.card ?? null;
  const startingCard: RateCard | null = scope?.startingCard ?? null;

  const [form, setForm] = useState<CardForm | null>(null);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [historyRow, setHistoryRow] = useState<RateCardRow | null>(null);

  const loadedKey = scope ? `${row?.id ?? 'new'}:${row?.version ?? 0}:${row?.isActive}` : 'loading';
  useEffect(() => {
    setForm(startingCard ? cardToForm(startingCard) : null);
    setErrors([]);
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedKey]);

  const draft = useMemo(() => (form ? formToCard(form) : null), [form]);
  const dirty = form ? isDirty(form, savedCard) : false;
  const set = (patch: Partial<CardForm>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['rate-cards'] });
    queryClient.invalidateQueries({ queryKey: ['rate-card-scope', vehicle.rideOption.id, zoneId] });
  };

  const saveMut = useMutation({
    // The row's version (active or removed earlier) guards against overwriting someone else's edit.
    mutationFn: () => saveRateCard({
      rideOptionId: vehicle.rideOption.id, zoneId, card: draft!, expectedVersion: row ? row.version : null, note: note.trim() || undefined,
    }),
    onSuccess: () => { toast.success('Rate card saved — applies to new bookings within seconds'); setErrors([]); refresh(); },
    onError: (e: any) => { setErrors(e?.response?.data?.errors ?? []); toast.error(getApiErrorMessage(e)); },
  });
  const removeMut = useMutation({
    mutationFn: (r: RateCardRow) => deactivateRateCard(r.id, r.version),
    onSuccess: () => { toast.success('Zone override removed — this zone now uses the global card'); refresh(); onRemoved?.(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (scopeQuery.isLoading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;
  if (scopeQuery.isError) return <Alert severity="error">{getApiErrorMessage(scopeQuery.error)}</Alert>;
  if (!form || !draft) return <Alert severity="warning">No card or legacy prices exist for this vehicle yet.</Alert>;

  const overriding = zoneId === null ? vehicle.zones.filter((z) => z.isActive) : [];
  const waitUncapped = Number(form.waitPerMin) > 0 && Number(form.waitFreeMin || 0) === 0 && form.waitCap.trim() === '';
  const statusChip = !row || !row.isActive
    ? <Chip size="small" label={zoneId === null ? 'Legacy prices (no card)' : 'Uses the global card'} />
    : row.isSeed ? <Chip size="small" label="Today's prices (unchanged)" /> : <Chip size="small" color="success" label={`Edited · v${row.version}`} />;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{vehicle.rideOption.label || vehicle.rideOption.type} · {scopeLabel}</Typography>
        <Box flex={1} />
        {statusChip}
        {row && <Button size="small" startIcon={<HistoryIcon />} onClick={() => setHistoryRow(row)}>History</Button>}
      </Stack>

      {overriding.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {overriding.length} zone{overriding.length > 1 ? 's have' : ' has'} its own {vehicle.rideOption.label} card and won't change when you save this one:{' '}
          {overriding.slice(0, 12).map((z) => `${z.zoneName}${z.city ? ` (${z.city})` : ''}`).join(', ')}{overriding.length > 12 ? ` +${overriding.length - 12} more` : ''}
        </Alert>
      )}
      {row?.isActive && !row.valid && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This saved card is invalid, so {zoneId === null ? 'this vehicle is priced with the legacy formula' : 'this zone uses the global card'} until it is fixed: {row.errors.join('; ')}
        </Alert>
      )}
      {zoneId !== null && !activeRow && (
        <Alert severity="info" sx={{ mb: 2 }}>No override here yet — starting from the global card. Nothing changes until you save.</Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Section title="Order fare" subtitle="Charged on every ride">
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Money label="Base fare" value={form.baseFare} onChange={(v) => set({ baseFare: v })} disabled={!canWrite} helper="For completing a ride" />
                <Money label="Minimum fare" value={form.minimumFare} optional onChange={(v) => set({ minimumFare: v })} disabled={!canWrite} helper="Empty = no floor (applied before surge)" />
              </Stack>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Distance fare</Typography>
                <Typography variant="caption" color="text.secondary">
                  Tiered: each km is charged at its own slab's rate (10 km on 0–6 ₹6.3 / 6+ ₹7.5 = 6×6.3 + 4×7.5). A ₹0 first slab = km included in the base fare.
                </Typography>
                <SlabTable slabs={form.slabs} onChange={(slabs) => set({ slabs })} disabled={!canWrite} />
              </Box>
              <Box sx={{ maxWidth: { sm: 320 } }}>
                <Money label="Time fare" value={form.timePerMin} onChange={(v) => set({ timePerMin: v })} disabled={!canWrite} unit="₹/min"
                       helper="Per minute of the trip time frozen at booking: the app's route time if sent, else distance ÷ RATE_CARD_AVG_SPEED_KMH (20 km/h); QR/agent use their ETA" />
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Money label="Pickup charge" value={form.pickupCharge} onChange={(v) => set({ pickupCharge: v })} disabled={!canWrite} helper="Flat, every ride" />
                <Money label="Driver additions" value={form.driverAdditions} onChange={(v) => set({ driverAdditions: v })} disabled={!canWrite} />
                <Money label="Congestion" value={form.congestionCharges} onChange={(v) => set({ congestionCharges: v })} disabled={!canWrite} />
              </Stack>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Wait time</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Money label="Free minutes" unit="min" value={form.waitFreeMin} onChange={(v) => set({ waitFreeMin: v })} disabled={!canWrite} />
                  <Money label="Per minute after" value={form.waitPerMin} onChange={(v) => set({ waitPerMin: v })} disabled={!canWrite} />
                  <Money label="Cap" value={form.waitCap} optional onChange={(v) => set({ waitCap: v })} disabled={!canWrite} helper="Empty = no cap" />
                </Stack>
                {list.waitBillingEnabled ? (
                  waitUncapped && <Alert severity="warning" sx={{ mt: 1 }}>Wait billing is ON: with 0 free minutes and no cap, riders pay from the first minute of waiting with no limit.</Alert>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Measured from the driver's arrival (50 m geofence) to ride start. Not billed until App Config WAIT_TIME_BILLING_ENABLED = 1 — set free minutes and a cap first.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Section>

          <Section title="Extra fare" subtitle="Not applicable on all rides">
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Long pickup</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Money label="Free km" unit="km" value={form.lpFreeKm} onChange={(v) => set({ lpFreeKm: v })} disabled={!canWrite} />
                  <Money label="Per km after" value={form.lpPerKm} onChange={(v) => set({ lpPerKm: v })} disabled={!canWrite} />
                  <Money label="Cap" value={form.lpCap} optional onChange={(v) => set({ lpCap: v })} disabled={!canWrite} helper="Empty = no cap" />
                </Stack>
                <Typography variant="caption" color="text.secondary">Driver's distance to the pickup when they accept (straight line × road factor). Added to the final bill, not the quote.</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Night fare ({String(list.nightWindow.startHour).padStart(2, '0')}:00–{String(list.nightWindow.endHour).padStart(2, '0')}:00, from App Config)
                </Typography>
                <RadioGroup row value={form.nightMode} onChange={(e) => set({ nightMode: e.target.value as CardForm['nightMode'] })}>
                  <FormControlLabel value="none" control={<Radio size="small" />} label="No night pricing" disabled={!canWrite} />
                  <FormControlLabel value="percent" control={<Radio size="small" />} label="Percentage" disabled={!canWrite} />
                  <FormControlLabel value="rates" control={<Radio size="small" />} label="Separate night rates" disabled={!canWrite} />
                </RadioGroup>
                {form.nightMode === 'percent' && (
                  <Stack spacing={1}>
                    <Box sx={{ maxWidth: 200 }}>
                      <Money label="Night surcharge" unit="%" value={form.nightPct} onChange={(v) => set({ nightPct: v })} disabled={!canWrite} />
                    </Box>
                    <Stack direction="row" flexWrap="wrap">
                      {(Object.keys(NIGHT_COMPONENT_LABELS) as NightComponent[]).map((c) => (
                        <FormControlLabel key={c} disabled={!canWrite} label={NIGHT_COMPONENT_LABELS[c]} control={
                          <Checkbox size="small" checked={form.nightAppliesTo.includes(c)} onChange={(e) =>
                            set({ nightAppliesTo: e.target.checked ? [...form.nightAppliesTo, c] : form.nightAppliesTo.filter((x) => x !== c) })} />
                        } />
                      ))}
                    </Stack>
                  </Stack>
                )}
                {form.nightMode === 'rates' && (
                  <Stack spacing={1}>
                    <Box sx={{ maxWidth: 240 }}>
                      <Money label="Night base fare" value={form.nightBaseFare} optional onChange={(v) => set({ nightBaseFare: v })} disabled={!canWrite} helper="Empty = day base fare" />
                    </Box>
                    <FormControlLabel disabled={!canWrite} control={<Switch checked={form.nightSlabsOn} onChange={(e) => set({ nightSlabsOn: e.target.checked })} />} label="Separate night distance slabs" />
                    {form.nightSlabsOn && <SlabTable slabs={form.nightSlabs} onChange={(nightSlabs) => set({ nightSlabs })} disabled={!canWrite} />}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Section>

          {errors.length > 0 && <Alert severity="error" sx={{ mb: 2 }}>{errors.map((e) => <div key={e}>{e}</div>)}</Alert>}

          {canWrite && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField size="small" label="Change note (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} inputProps={{ maxLength: 500 }} />
              <Button variant="outlined" disabled={!dirty || saveMut.isPending} onClick={() => startingCard && setForm(cardToForm(startingCard))}>Discard</Button>
              <Button variant="contained" disabled={!dirty || saveMut.isPending} onClick={async () => {
                const ok = await confirm({ title: 'Save rate card', message: `New prices apply to NEW bookings only (rides already booked keep their fare). ${vehicle.rideOption.label} · ${scopeLabel}.`, confirmText: 'Save' });
                if (ok) saveMut.mutate();
              }}>
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </Button>
              {zoneId !== null && activeRow && (
                <Button color="error" disabled={removeMut.isPending} onClick={async () => {
                  const ok = await confirm({ title: 'Remove zone override', message: `${scopeLabel} will use the global ${vehicle.rideOption.label} card for new bookings.`, destructive: true, confirmText: 'Remove' });
                  if (ok) removeMut.mutate(activeRow);
                }}>Remove override</Button>
              )}
            </Stack>
          )}
        </Box>
        <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
          <FarePreview draft={draft} saved={savedCard} />
        </Box>
      </Stack>
      <HistoryDialog row={historyRow} onClose={() => setHistoryRow(null)} />
    </Box>
  );
};

export default RateCardEditor;
